import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/lib/Seo";
import PageHero from "@/components/common/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACTS, CENTRES } from "@/data/site";
import { easeOut } from "@/components/common/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", sujet: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.email || !form.message) {
      toast.error("Merci de renseigner votre nom, email et message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, consent: true });
      setSent(true);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      setForm({ nom: "", email: "", telephone: "", sujet: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact"
        description="Contactez EDUCASER GROUP : formulaire de contact, téléphones, emails et adresses de nos centres de Palaiseau, Noisy-le-Sec, Meaux et Choisy-le-Roi."
        path="/contact"
      />
      <PageHero
        breadcrumb={[{ label: "Accueil", to: "/" }, { label: "Contact" }]}
        eyebrow="Nous contacter"
        title="Parlons de votre projet"
        intro="Une question sur nos formations ou leur financement ? Écrivez-nous, nous vous répondons rapidement."
      />

      <div className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 lg:grid-cols-12 lg:px-8">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_16px_48px_rgba(0,35,102,0.08)] lg:p-10">
              {sent ? (
                <div className="py-10 text-center" data-testid="contact-success">
                  <CheckCircle2 className="mx-auto h-16 w-16 text-brand-red" />
                  <h2 className="mt-5 font-display text-2xl font-bold text-brand-blue">Message envoyé !</h2>
                  <p className="mt-2 text-slate-500">Merci, notre équipe revient vers vous dans les meilleurs délais.</p>
                  <Button className="mt-6 rounded-full bg-brand-blue hover:bg-brand-blue-light" onClick={() => setSent(false)}>
                    Envoyer un autre message
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="c-nom">Nom complet *</Label>
                      <Input id="c-nom" name="nom" value={form.nom} onChange={onChange} className="mt-1.5" placeholder="Votre nom" data-testid="contact-nom" />
                    </div>
                    <div>
                      <Label htmlFor="c-email">Email *</Label>
                      <Input id="c-email" name="email" type="email" value={form.email} onChange={onChange} className="mt-1.5" placeholder="email@exemple.fr" data-testid="contact-email" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="c-tel">Téléphone</Label>
                      <Input id="c-tel" name="telephone" value={form.telephone} onChange={onChange} className="mt-1.5" placeholder="06 00 00 00 00" data-testid="contact-telephone" />
                    </div>
                    <div>
                      <Label htmlFor="c-sujet">Sujet</Label>
                      <Input id="c-sujet" name="sujet" value={form.sujet} onChange={onChange} className="mt-1.5" placeholder="Objet de votre demande" data-testid="contact-sujet" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c-msg">Message *</Label>
                    <Textarea id="c-msg" name="message" value={form.message} onChange={onChange} rows={5} className="mt-1.5" placeholder="Votre message" data-testid="contact-message" />
                  </div>
                  <Button type="submit" disabled={loading} className="group w-full rounded-full bg-brand-red py-6 text-base font-semibold hover:bg-brand-red-light" data-testid="contact-submit">
                    {loading ? "Envoi..." : "Envoyer le message"}
                    <Send className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </form>
              )}
            </div>
          </div>

          {/* Coordinates */}
          <div className="lg:col-span-5">
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: easeOut }}
                className="rounded-3xl bg-brand-blue p-8 text-white"
              >
                <h3 className="font-display text-lg font-bold">{CONTACTS.enseignants.label}</h3>
                <ul className="mt-4 space-y-3 text-sm text-white/80">
                  <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-brand-red-light" /> {CONTACTS.enseignants.telephone}</li>
                  <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-brand-red-light" /> {CONTACTS.enseignants.email}</li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: easeOut, delay: 0.1 }}
                className="rounded-3xl border border-slate-200 p-8"
              >
                <h3 className="font-display text-lg font-bold text-brand-blue">{CONTACTS.groupeLourd.label}</h3>
                <ul className="mt-4 space-y-3 text-sm text-slate-600">
                  <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-brand-red" /> {CONTACTS.groupeLourd.telephone}</li>
                  <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-brand-red" /> {CONTACTS.groupeLourd.email}</li>
                </ul>
              </motion.div>

              {/* Static map — centres listés */}
              <div className="relative flex h-40 items-center justify-center overflow-hidden rounded-3xl bg-brand-blue" data-testid="contact-static-map">
                <div className="absolute inset-0 bg-grid opacity-10" />
                <div className="relative grid grid-cols-2 gap-4 px-6 text-center text-white sm:grid-cols-4">
                  {CENTRES.map((c) => (
                    <div key={c.id}>
                      <MapPin className="mx-auto h-6 w-6 text-brand-red-light" />
                      <p className="mt-1 text-xs font-medium">{c.ville}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
